import { useState, useMemo, useEffect } from 'react';
import {
  useCurrentWallet,
  thirdPartyWallet,
  useChainList,
  useConnectWalletModal,
  useCreateWalletModal,
  useCloudWallet,
} from '@web3fe/infra';
import { WalletType2 } from '@/types/wallet';
import { WalletInfo } from '@/types/wallet';
import { WalletAddress } from '@/types/account';
import { WalletChainTypes } from '@web3/base-utils';
import { monitorLog } from '@web3/base-tracking';
import { useCurrentToken } from '@/containers/pool/operation/stake/hooks';
import { btcFilterCurrentWalletInfo } from '@/containers/pool/operation/stake/third/btcFilterCurrentWalletInfo';
import { isLBTCvPool, isLombardPool } from '@/utils/common';
import { usePoolId } from './usePoolInfo';
import { BtcAddressListItem } from '@/types/wallet';

export const useWallet = () => {
  const {
    isNone,
    walletIsReady,
    isCloudWallet,
    name,
    isThirdParty,
    switchToBrowser,
    switchToBybitExtension,
    evmAddress,
    solAddress,
    btcAddress,
    stxAddress,
    suiAddress,
    tonAddress,
    btcAddressList,
  } = useCurrentWallet();

  const { show: connectWalletShow } = useConnectWalletModal();
  const { show: createWalletShow } = useCreateWalletModal();

  const { cloudWalletList } = useCloudWallet();
  const { mnemonicChainList, mnemonicChainMap, chainMap } = useChainList();

  const currentWalletType = (() => {
    if (isCloudWallet) {
      return WalletType2.CLOUD;
    }

    if (isThirdParty && thirdPartyWallet.isBybitWalletExtension) {
      return thirdPartyWallet.activeWalletHub?.walletType;
    }

    if (isThirdParty && !thirdPartyWallet.isBybitWalletExtension) {
      return WalletType2.THIRD_PARTY;
    }

    return WalletType2.NOT;
  })();

  const walletAddresses = useMemo(() => {
    const addresses: WalletAddress[] = [];
    if (isThirdParty) {
      mnemonicChainList.forEach((item) => {
        let address = evmAddress;
        if (item.chainType === WalletChainTypes.BTC) {
          address = btcAddress;
        } else if (item.chainType === WalletChainTypes.SOLANA) {
          address = solAddress;
        } else if (item.chainType === WalletChainTypes.SUI) {
          address = suiAddress;
        } else if (item.chainType === WalletChainTypes.STACKS) {
          address = stxAddress;
        } else if (item.chainType === WalletChainTypes.EVM) {
          address = evmAddress;
        } else if (item.chainType === WalletChainTypes.TON) {
          address = tonAddress;
        }
        if (address) {
          addresses.push({
            walletAddress: address,
            chainId: +item.chainId,
          });
        }
      });
    } else if (isCloudWallet) {
      addresses.push(
        ...cloudWalletList.map((item) => ({
          walletAddress: item.address,
          chainId: +item.chainId,
        }))
      );
    }

    return addresses;
  }, [chainMap, isThirdParty, isCloudWallet, stxAddress]);

  return {
    tonWalletInfo: {
      address: tonAddress,
      symbol: name,
      chainId: thirdPartyWallet.activeWalletHub?.TON?._chainId ?? 999999991,
      pubKey: thirdPartyWallet.activeWalletHub?.TON?.publicKey,
    } as WalletInfo,
    suiWalletInfo: {
      address: suiAddress,
      symbol: name,
      chainId: thirdPartyWallet.activeWalletHub?.SUI?._chainId ?? 999999998,
      pubKey: thirdPartyWallet.activeWalletHub?.SUI?.publicKey,
    } as WalletInfo,
    solanaWalletInfo: {
      address: solAddress,
      symbol: name,
      chainId: thirdPartyWallet.activeWalletHub?.SOL?._chainId ?? 999999997,
      pubKey: thirdPartyWallet.activeWalletHub?.SOL?.publicKey,
    } as WalletInfo,
    evmWalletInfo: {
      address: evmAddress,
      symbol: name,
      chainId: thirdPartyWallet.activeWalletHub?.evmChainId,
      pubKey: '',
    } as WalletInfo,
    stxWalletInfo: {
      address: stxAddress,
      pubKey: thirdPartyWallet?.activeWalletHub?.STX?.publicKey,
      symbol: name,
      chainId: 999999996,
    } as WalletInfo,
    btcWalletInfo: {
      address: btcAddress,
      pubKey: thirdPartyWallet?.activeWalletHub?.BTC?.publicKey,
      symbol: name,
      btcAddressList,
      chainId: thirdPartyWallet?.activeWalletHub?.BTC?._chainId ?? 1,
    } as WalletInfo,
    currentWalletType,
    walletIsReady,
    isThird: isThirdParty,
    isLogin: !isNone,
    switchToBrowser,
    chainMap: { ...mnemonicChainMap, ...chainMap },
    evmThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.EVM?.provider,
    stxThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.STX?.provider,
    solanaThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.SOL?.provider,
    suiThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.SUI?.provider,
    tonThirdPartyProvider: thirdPartyWallet?.activeWalletHub?.TON?.provider,
    suiAccount: thirdPartyWallet?.activeWalletHub?.SUI?.accounts?.[0] || {},
    walletAddresses,
    isBybitWalletExtension:
      isThirdParty && thirdPartyWallet.isBybitWalletExtension,
    openLoginModal: () => {
      connectWalletShow();
    },
    createWallet: () => {
      createWalletShow();
    },
    connectBybitWallet: (walletType: WalletType2) => {
      switchToBybitExtension(walletType);
    },
  };
};

export const useWalletId = () => {
  const { isBybitWalletExtension, walletAddresses } = useWallet();
  const [walletId, setWalletId] = useState('');

  const fetchWalletId = async () => {
    if (isBybitWalletExtension) {
      try {
        const bybitWalletInfo =
          (await window.top?.bybitWallet?.request({
            method: 'getWalletInfo',
          })) || ({} as any);
        setWalletId(bybitWalletInfo.walletId);
      } catch (e) {
        console.log(e);
      }
    }
  };

  useEffect(() => {
    if (isBybitWalletExtension) {
      fetchWalletId();
    }
    if (!isBybitWalletExtension) {
      setWalletId('');
    }
  }, [isBybitWalletExtension, walletAddresses]);
  return {
    walletId,
  };
};

export const useChain = () => {
  const { evmWalletInfo } = useWallet();

  const switchToPoolChain = async (chainId: number, type?: string) => {
    if (chainId !== evmWalletInfo.chainId) {
      try {
        await thirdPartyWallet?.activeWalletHub?.EVM?.switchChain(chainId);
        return true;
      } catch (e) {
        monitorLog({
          t: `${type}Result`,
          ext: e?.message,
          chainId,
        });
        return false;
      }
    }

    return true;
  };

  return {
    switchToPoolChain,
  };
};

export const useCurrentChainAddress = (chainCode: string) => {
  const {
    evmWalletInfo,
    stxWalletInfo,
    solanaWalletInfo,
    suiWalletInfo,
    tonWalletInfo,
    btcWalletInfo,
  } = useWallet();
  const { currentStakeToken } = useCurrentToken();
  const poolId = usePoolId();

  if (chainCode === 'STX') {
    return {
      walletAddress: stxWalletInfo.address,
      pubKey: stxWalletInfo.pubKey,
    };
  }
  if (chainCode === 'SOL') {
    return {
      walletAddress: solanaWalletInfo.address,
      pubKey: solanaWalletInfo.pubKey,
    };
  }
  if (chainCode === 'SUI') {
    return {
      walletAddress: suiWalletInfo.address,
      pubKey: suiWalletInfo.pubKey,
    };
  }

  if (chainCode === 'TON') {
    return {
      walletAddress: tonWalletInfo.address,
      pubKey: tonWalletInfo.pubKey,
    };
  }

  if (chainCode === 'BTC') {
    if (isLombardPool(poolId) || isLBTCvPool(poolId)) {
      const btcAddressListItem = btcFilterCurrentWalletInfo(
        btcWalletInfo.btcAddressList,
        currentStakeToken
      );

      return {
        walletAddress: btcAddressListItem.address,
        pubKey: btcAddressListItem.publicKey,
      };
    }

    // 兜底用，实际不会走到这里
    return {
      walletAddress: btcWalletInfo.address,
      pubKey: btcWalletInfo.pubKey,
    };
  }

  return {
    walletAddress: evmWalletInfo.address,
    pubKey: stxWalletInfo.pubKey,
  };
};

export const getBtcInfoByChainCode = (
  chainCode: string,
  btcAddressList: BtcAddressListItem[]
) => {
  const btcAddressInfo = btcAddressList.filter(
    (item) => String(item.chainCode) === String(chainCode)
  )[0];
  return {
    chainId: Number(btcAddressInfo.chainId),
    btcAddress: btcAddressInfo.address,
    chainCode,
  };
};
